import { useState, useEffect, useRef } from "react";
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  getAuth,
} from "firebase/auth";
import {
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { useLanguage } from "../i18n/LanguageProvider";
import { sendPasswordChangedSecurityEmail } from "../authService";

async function sha256Hex(value) {
  const encoder = new TextEncoder();
  const hashBuffer = await window.crypto.subtle.digest(
    "SHA-256",
    encoder.encode(String(value || ""))
  );

  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function ResetPasswordPage() {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [validCode, setValidCode] = useState(false);

  const [password, setPassword] = useState("");
  const [passwordRepeat, setPasswordRepeat] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const securityMailSentForHashRef = useRef(null);

  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    const verifyCode = async () => {
      try {
        const email = await verifyPasswordResetCode(
          getAuth(),
          oobCode
        );

        setVerifiedEmail(String(email || "").trim());
        setValidCode(true);
      } catch (err) {
        setError(t("resetPasswordPage.errors.invalidOrExpired"));
      }

      setLoading(false);
    };

    verifyCode();
  }, [oobCode, t]);

  const handleReset = async () => {
    setError("");
    if (submitting) return;

    if (password.length < 6) {
      setError(t("resetPasswordPage.errors.passwordTooShort"));
      return;
    }

    if (password !== passwordRepeat) {
      setError(t("resetPasswordPage.errors.passwordMismatch"));
      return;
    }

    try {
      setSubmitting(true);
      await confirmPasswordReset(
        getAuth(),
        oobCode,
        password
      );

      setSuccess(true);

      if (verifiedEmail && oobCode && window.crypto?.subtle) {
        try {
          const resetCodeHash = await sha256Hex(oobCode);

          if (securityMailSentForHashRef.current !== resetCodeHash) {
            securityMailSentForHashRef.current = resetCodeHash;
            await sendPasswordChangedSecurityEmail({
              email: verifiedEmail,
              language,
              resetCodeHash,
            });
          }
        } catch (mailError) {
          console.warn("Password changed security email could not be sent:", {
            code: mailError?.code || null,
            message: mailError?.message || null,
          });
        }
      }
    } catch (err) {
      setError(t("resetPasswordPage.errors.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
  return (
    <div style={{ color: "white" }}>
      {t("resetPasswordPage.loading")}
    </div>
  );
}

  return (
  <div
    style={{
      height: "100dvh",
      display: "flex",
      justifyContent: "center",
      alignItems: "stretch",
      background: "#000",
      padding: 0,
      boxSizing: "border-box",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: "430px",
        height: "100dvh",
        borderRadius: 0,
        overflow: "hidden",
        background: "linear-gradient(90deg, #080427 0%, #020047 100%)",
        position: "relative",
        padding: "calc(26px + var(--vq-safe-area-top)) calc(18px + var(--vq-safe-area-right)) calc(26px + var(--vq-safe-area-bottom)) calc(18px + var(--vq-safe-area-left))",
        boxSizing: "border-box",
        fontFamily:
          "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        color: "white",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: 10,
        }}
      >
        <img
          src="/vaultquest-logo.png"
          alt="VaultQuest"
          style={{
            width: 180,
            height: 180,
            objectFit: "contain",
            marginBottom: 10,
          }}
        />

        <div
          style={{
            fontSize: 18,
            fontWeight: 1000,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            textAlign: "center",
            marginBottom: 10,
          }}
        >
          {t("resetPasswordPage.title")}
        </div>
      </div>

      {!validCode ? (
  <>
    <div
      style={{
        marginTop: 30,
        padding: 14,
        borderRadius: 14,
        background: "rgba(255,60,60,0.18)",
        border: "1px solid rgba(255,60,60,0.55)",
        textAlign: "center",
        fontWeight: 900,
      }}
    >
      {error}
    </div>

    <button
      onClick={() => navigate("/login")}
      style={{
        marginTop: 20,
        width: "100%",
        height: 70,
        borderRadius: 20,
        border: "none",
        background:
          "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
        color: "white",
        fontWeight: 1000,
        fontSize: 18,
        textTransform: "uppercase",
        cursor: "pointer",
      }}
    >
      {t("resetPasswordPage.toLogin")}
    </button>
  </>
      ) : success ? (
        <>
          <div
            style={{
              marginTop: 30,
              padding: 14,
              borderRadius: 14,
              background: "rgba(0,255,120,0.15)",
              border: "1px solid rgba(0,255,120,0.4)",
              textAlign: "center",
              fontWeight: 900,
            }}
          >
            {t("resetPasswordPage.success")}
          </div>

          <button
            onClick={() => navigate("/login")}
            style={{
              marginTop: 20,
              width: "100%",
              height: 70,
              borderRadius: 20,
              border: "none",
              background:
                "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
              color: "white",
              fontWeight: 1000,
              fontSize: 18,
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            {t("resetPasswordPage.toLogin")}
          </button>
        </>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gap: 14,
              marginTop: 20,
            }}
          >
            <div
              style={{
                height: 62,
                borderRadius: 18,
                background: "white",
                display: "flex",
                alignItems: "center",
                padding: "0 18px",
                boxSizing: "border-box",
              }}
            >
              <input
                type="password"
                placeholder={t("resetPasswordPage.newPasswordPlaceholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  width: "100%",
                  fontSize: 16,
                  fontWeight: 900,
                  color: "#222",
                  background: "transparent",
                }}
              />
            </div>

            <div
              style={{
                height: 62,
                borderRadius: 18,
                background: "white",
                display: "flex",
                alignItems: "center",
                padding: "0 18px",
                boxSizing: "border-box",
              }}
            >
              <input
                type="password"
                placeholder={t("resetPasswordPage.repeatPasswordPlaceholder")}
                value={passwordRepeat}
                onChange={(e) => setPasswordRepeat(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  width: "100%",
                  fontSize: 16,
                  fontWeight: 900,
                  color: "#222",
                  background: "transparent",
                }}
              />
            </div>

            <button
              onClick={handleReset}
              disabled={submitting}
              style={{
                height: 72,
                borderRadius: 20,
                border: "none",
                background:
                  "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
                color: "white",
                fontWeight: 1000,
                fontSize: 22,
                textTransform: "uppercase",
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.65 : 1,
              }}
            >
              {t("resetPasswordPage.submit")}
            </button>

            {error && (
              <div
                style={{
                  padding: 12,
                  borderRadius: 14,
                  background: "rgba(255,60,60,0.18)",
                  border: "1px solid rgba(255,60,60,0.55)",
                  textAlign: "center",
                  fontWeight: 900,
                }}
              >
                {error}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  </div>
);
}

export default ResetPasswordPage;
